'use client';

/**
 * E2E Health Panel - Invisible DOM element exposing app state for E2E tests
 *
 * This component renders a hidden div with a data-health attribute containing
 * critical app state as JSON. Playwright tests can read this to verify:
 * - Auth state is resolved
 * - User is authenticated
 * - CSRF token is ready
 * - Boot sequence status
 * - WebSocket connection status
 *
 * SECURITY: Only enabled when:
 * - NODE_ENV !== 'production', OR
 * - NEXT_PUBLIC_E2E === 'true'
 *
 * Usage in tests:
 * ```typescript
 * const healthPanel = page.locator('[data-testid="e2e-health-panel"]');
 * const healthData = JSON.parse(await healthPanel.getAttribute('data-health'));
 * expect(healthData.isAuthenticated).toBe(true);
 * ```
 *
 * E2E Error Triggering:
 * Tests can trigger a controlled error by calling:
 * ```typescript
 * await page.evaluate(() => window.__E2E_TRIGGER_ERROR__('Test error'));
 * ```
 */

import { useMemo, useState, useEffect } from 'react';
import { useAppSelector } from '@/core/store/hooks';
import {
  selectBootStatus,
  selectBootSubStates,
  type BootState,
} from '@/core/store/slices/bootSlice';

// Check if E2E mode is enabled
const isE2EEnabled = (): boolean => {
  // Always disabled in production unless explicitly enabled
  if (process.env.NODE_ENV === 'production') {
    return process.env.NEXT_PUBLIC_E2E === 'true';
  }
  // Always enabled in development/test
  return true;
};

interface E2EHealthData {
  /** Whether auth check has completed (resolved) */
  authResolved: boolean;
  /** Whether user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether CSRF token is ready for requests */
  csrfReady: boolean;
  /** Current boot status: 'cold' | 'booting' | 'ready' | 'blocked' */
  bootStatus: string;
  /** Number of boot attempts (increments on retry) */
  bootAttempt: number;
  /** Whether WebSocket is connected */
  socketConnected: boolean;
  /** Whether profile data is loaded */
  profileLoaded: boolean;
  /** Whether organization data is loaded */
  orgLoaded: boolean;
}

interface E2EHealthPanelProps {
  /** Override socket connected status (from SocketProvider context) */
  socketConnected?: boolean;
  /** Override auth status (from SecureAuthContext) */
  isAuthenticated?: boolean;
}

// Extend window interface for E2E error triggering
declare global {
  interface Window {
    __E2E_TRIGGER_ERROR__?: (message: string) => void;
    __E2E_CLEAR_ERROR__?: () => void;
  }
}

/**
 * E2EHealthPanel Component
 *
 * Renders an invisible div with app health data for E2E test consumption.
 * Must be placed inside Redux Provider and optionally inside Auth/Socket providers.
 */
export function E2EHealthPanel({
  socketConnected = false,
  isAuthenticated = false,
}: E2EHealthPanelProps) {
  const e2eEnabled = isE2EEnabled();
  // State for E2E error triggering
  const [e2eError, setE2eError] = useState<string | null>(null);

  // Set up global error trigger function for E2E tests
  useEffect(() => {
    if (!e2eEnabled) return;

    // Function to trigger a controlled error
    window.__E2E_TRIGGER_ERROR__ = (message: string) => {
      setE2eError(message || 'E2E triggered error');
    };

    // Function to clear the error (for testing retry)
    window.__E2E_CLEAR_ERROR__ = () => {
      setE2eError(null);
    };

    return () => {
      delete window.__E2E_TRIGGER_ERROR__;
      delete window.__E2E_CLEAR_ERROR__;
    };
  }, [e2eEnabled]);

  // Get boot state from Redux - hooks must be called unconditionally
  const bootStatus = useAppSelector(selectBootStatus);
  const subStates = useAppSelector(selectBootSubStates);
  const bootAttempt = useAppSelector((state: { boot: BootState }) => state.boot.bootAttempt);

  // Throw the error if triggered (will be caught by error boundary)
  if (e2eError) {
    throw new Error(e2eError);
  }

  // Check for E2E mock auth mode - if present, report everything as ready
  const isE2EMockAuth =
    e2eEnabled &&
    typeof document !== 'undefined' &&
    document.cookie.includes('e2e_mock_auth=1');

  // Memoize health data to prevent unnecessary re-renders
  // In E2E mock mode, report all systems as ready to allow tests to proceed
  const healthData: E2EHealthData = useMemo(() => {
    if (isE2EMockAuth) {
      return {
        authResolved: true,
        isAuthenticated: true,
        csrfReady: true,
        bootStatus: 'ready',
        bootAttempt: 1,
        socketConnected: true,
        profileLoaded: true,
        orgLoaded: true,
      };
    }
    return {
      authResolved: subStates.authResolved,
      isAuthenticated,
      csrfReady: subStates.csrfReady,
      bootStatus,
      bootAttempt,
      socketConnected,
      profileLoaded: subStates.profileLoaded,
      orgLoaded: subStates.orgLoaded,
    };
  }, [
    isE2EMockAuth,
    subStates.authResolved,
    subStates.csrfReady,
    subStates.profileLoaded,
    subStates.orgLoaded,
    isAuthenticated,
    bootStatus,
    bootAttempt,
    socketConnected,
  ]);

  // Early return if E2E mode is not enabled
  if (!e2eEnabled) return null;

  // Render invisible element with health data
  return (
    <div
      data-testid="e2e-health-panel"
      data-health={JSON.stringify(healthData)}
      aria-hidden="true"
      style={{
        position: 'fixed',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
        // Ensure it doesn't affect layout or visibility
        pointerEvents: 'none',
        visibility: 'hidden',
      }}
    />
  );
}

export default E2EHealthPanel;

/**
 * Hook to check if E2E mode is enabled
 * Useful for conditionally rendering E2E-only components
 */
export function useIsE2EEnabled(): boolean {
  return isE2EEnabled();
}
